import React, { useRef, useState, useEffect, useMemo } from 'react';
import { 
  Grid, 
  GridColumn, 
  GridItemChangeEvent,
  GridCellProps,
  GridHandle,
  GridSelectionChangeEvent,
  GridHeaderSelectionChangeEvent
} from "@progress/kendo-react-grid";
import { EditDescriptor, SelectDescriptor } from '@progress/kendo-react-data-tools';
import { Groups, GreasePumpStatus } from '../../../../Models/Assets/AssetTemplateReadDto';
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, NumericTextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import useIdentityStore from '../../../../Zustand/identityStore';

/** Gateways from step 1 - each grease pump can be assigned to one of these */
interface GatewayOption {
  id: string;
  serialNumber: string;
}

interface Props {
  greasePumps: any[];
  onGreasePumpsChange: (updatedGroups: Groups[]) => void;
  assetData?: any;
  onSelectionChange?: (selectedGreasePumpId: string | null) => void;
  selectedGreasePumpId?: string | null;
  /** Gateways selected in previous step - dropdown options for device column */
  gateways?: GatewayOption[] | { id: string; name?: string; serialNumber?: string }[];
}

const DATA_ITEM_KEY = 'id';

const allInEdit = (greasePumps: any[]) => {
  const result: EditDescriptor = {};
  const editedColumns = ['status', 'deviceId', 'timingOffsetSeconds'];
  greasePumps.forEach((pump) => {
    result[pump.id] = editedColumns;
  });
  return result;
};

const statusOptions: { label: string; value: GreasePumpStatus }[] = [
  { label: 'Enabled', value: 'Active' },
  { label: 'Disabled', value: 'Inactive' }
];

function pumpStatus(pump: any): GreasePumpStatus {
  return pump.status ?? 'Inactive';
}

/** Normalize gateways to { id, serialNumber } for dropdown */
function toGatewayOptions(gateways: Props['gateways']): GatewayOption[] {
  if (!gateways?.length) return [];
  return gateways.map((gw: any) => ({
    id: gw.id || '',
    serialNumber: gw.serialNumber ?? gw.name ?? ''
  })).filter(g => g.id);
}

const GreasePumpsTableComponent: React.FC<Props> = ({
  greasePumps,
  onGreasePumpsChange,
  assetData = { endpoints: [] },
  onSelectionChange,
  selectedGreasePumpId,
  gateways = []
}) => {
  const gridRef = useRef<GridHandle>(null);
  const greasePumpsRef = useRef(greasePumps);
  greasePumpsRef.current = greasePumps;
  const onGreasePumpsChangeRef = useRef(onGreasePumpsChange);
  onGreasePumpsChangeRef.current = onGreasePumpsChange;

  const edit = useMemo(() => allInEdit(greasePumps), [greasePumps]);
  const [select, setSelect] = useState<SelectDescriptor>({});
  const localizationService = useLocalization();
  const isSuperAdmin = useIdentityStore((x) => x.user?.profile?.role) === 'SuperAdmin';

  // Select the first grease pump by default
  useEffect(() => {
    if (greasePumps.length > 0 && Object.keys(select).length === 0) {
      setSelect({ [greasePumps[0].id]: true });
    }
  }, [greasePumps, select]);

  // Sync external selection with internal table selection
  useEffect(() => {
    if (selectedGreasePumpId) {
      setSelect({ [selectedGreasePumpId]: true });
    }
  }, [selectedGreasePumpId]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    // Check if dataItem exists and has an id
    if (!event.dataItem || !event.dataItem.id) {
      return;
    }
    
    const newGreasePumps = greasePumps.map((pump: any) =>
      pump.id === event.dataItem.id ? { ...pump, [event.field!]: event.value } : pump
    );
    
    // Notify parent component
    onGreasePumpsChange(newGreasePumps);
  };

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    // Check if dataItem exists and has an id
    if (!event.dataItem || !event.dataItem.id) {
      return;
    }
    
    const newPumpId = event.dataItem.id;
    setSelect({ [newPumpId]: true });
    
    // Notify parent component about selection change
    onSelectionChange?.(newPumpId);
  };

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent) => {
    if (event.nativeEvent.target.checked) {
      // Select all
      const allSelected = {};
      greasePumps.forEach(pump => {
        allSelected[pump.id] = true;
      });
      setSelect(allSelected);
    } else {
      // Deselect all
      setSelect({});
      onSelectionChange?.(null);
    }
  };

  const channelCell = (props: GridCellProps) => {
    const { dataItem } = props;
    // Channel is read-only (fixed)
    const currentChannel = dataItem.devices && dataItem.devices.length > 0
      ? dataItem.devices[0].channel || 0
      : dataItem.channelNo || 0;
    return <td>{currentChannel}</td>;
  };

  const gatewayOptions = toGatewayOptions(gateways);
  const notConfigured = localizationService.toLanguageString('custom.notConfigured', enMessages.custom.notConfigured);
  const gatewayDropdownData = [
    { id: '', serialNumber: notConfigured },
    ...gatewayOptions
  ];

  const deviceIdCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const currentDeviceId = dataItem.deviceId || '';
    const currentSerial = dataItem.devices?.[0]?.serialNumber ?? dataItem.serialNo ?? '';
    const deviceDisplay = currentSerial || (gatewayOptions.find(g => g.id === currentDeviceId)?.serialNumber) || notConfigured;
    const currentValue = gatewayDropdownData.find(g => g.id === currentDeviceId)
      ?? (currentDeviceId ? { id: currentDeviceId, serialNumber: currentSerial || deviceDisplay } : gatewayDropdownData[0]);

    // Filter out devices already selected in other rows (allow current row's selection)
    const usedDeviceIds = new Set(
      greasePumps
        .filter(p => p.id !== dataItem.id)
        .map(p => p.deviceId)
        .filter(Boolean)
    );
    const availableDevices = gatewayDropdownData.filter(
      g => g.id === '' || g.id === currentDeviceId || !usedDeviceIds.has(g.id)
    );
    const displayData = availableDevices.some(d => d.id === currentValue?.id)
      ? availableDevices
      : [currentValue, ...availableDevices];

    const handleDeviceChange = (e: DropDownListChangeEvent) => {
      const selected = e.target.value;
      const newGreasePumps = greasePumps.map((pump: any) =>
        pump.id === dataItem.id
          ? {
              ...pump,
              deviceId: selected?.id || '',
              serialNo: selected?.id ? (selected?.serialNumber || '') : ''
            }
          : pump
      );
      onGreasePumpsChange(newGreasePumps);
    };

    return (
      <td>
        <DropDownList
          style={{ width: '100%' }}
          popupSettings={{ width: 180 }}
          data={displayData}
          textField="serialNumber"
          dataItemKey="id"
          value={currentValue}
          onChange={handleDeviceChange}
          filterable
        />
      </td>
    );
  };

  // useMemo with empty deps keeps a stable component reference across renders,
  // preventing focus loss on keystroke. Refs provide access to latest data.
  const timingAdjustmentCell = useMemo(() => (props: GridCellProps) => {
    const { dataItem } = props;
    return (
      <td>
        <NumericTextBox
          style={{ width: '100%' }}
          value={dataItem.timingOffsetSeconds ?? 0}
          onChange={(e: NumericTextBoxChangeEvent) => {
            if (!dataItem?.id) return;
            const updated = greasePumpsRef.current.map((pump: any) =>
              pump.id === dataItem.id ? { ...pump, timingOffsetSeconds: e.value ?? 0 } : pump
            );
            onGreasePumpsChangeRef.current(updated);
          }}
          format="n0"
          step={1}
        />
      </td>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const TimingAdjustmentHeaderCell = (props: any) => (
    <Tooltip
      anchorElement="target"
      position="bottom"
      content={() => (
        <div style={{ maxWidth: 180 }}>
          Moves the start and end pump times earlier (−) or later (+) by the specified number of seconds.
        </div>
      )}
    >
      <span className="k-cell-inner">
        <span className="k-link">
          <span className="k-column-title" title=" ">{props.title}</span>
          {props.children}
        </span>
      </span>
    </Tooltip>
  );

  const EmptyHeaderCell = () => <div></div>;

  const statusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const handleDropDownChange = (e: DropDownListChangeEvent) => {
      handleItemChange({
        dataItem: dataItem,
        field: 'status',
        value: e.target.value.value
      } as GridItemChangeEvent);
    };
    return (
      <td>
        <DropDownList
          style={{ width: '100%' }}
          data={statusOptions}
          textField="label"
          dataItemKey="value"
          value={statusOptions.find(opt => opt.value === pumpStatus(dataItem)) ?? statusOptions.find(opt => opt.value === 'Inactive')}
          onChange={handleDropDownChange}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full checkbox-outline-soft-override">
      <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">{localizationService.toLanguageString('custom.greasePumps', enMessages.custom.greasePumps)}</div>
      </div>
      <div className="flex-1 min-h-0 overscroll-contain">
        <Grid
          ref={gridRef}
          data={greasePumps}
          style={{ height: '100%' }}
          dataItemKey={DATA_ITEM_KEY}
          onItemChange={handleItemChange}
          editable={true}
          defaultEdit={edit}
          selectable={{ enabled: true, mode: 'single' }}
          select={select}
          onSelectionChange={handleSelectionChange}
          onHeaderSelectionChange={handleHeaderSelectionChange}
        >
          <GridColumn 
            columnType="checkbox" 
            headerCell={EmptyHeaderCell}
          />
          <GridColumn 
            field="name" 
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
            editable={false}
          />
          <GridColumn 
            field="deviceId" 
            title={localizationService.toLanguageString('custom.device', enMessages.custom.device)}
            cells={{
              data: deviceIdCell
            }}
            editable={false}
          />
          <GridColumn
            field="status"
            title={localizationService.toLanguageString('custom.status', enMessages.custom?.status ?? 'Status')}
            cells={{
              data: statusCell
            }}
          />
          {isSuperAdmin && (
            <GridColumn
              field="timingOffsetSeconds"
              title="Timing Offset (secs)"
              headerCell={TimingAdjustmentHeaderCell}
              cells={{ data: timingAdjustmentCell }}
            />
          )}
          <GridColumn
            field="channel"
            title="CH"
            width={55}
            cells={{
              data: channelCell
            }}
            editable={false}
          />
        </Grid>
      </div>
    </div>
  );
};

export default GreasePumpsTableComponent; 